import {
  Button,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  Box,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {
  createPortfolioMonitoringConfigVersion,
  getLatestPortfolioMonitoringConfig,
  getPortfolioMonitoringConfigVersion,
  listPortfolioMonitoringConfigVersions,
} from 'api/portfolioMonitoringConfigApi';
import { mergePortfolioMonitoringMetrics } from 'api/portfolioMonitoringConfigDefaults';
import {
  PORTFOLIO_MONITORING_COLUMN_TOOLTIPS,
  PORTFOLIO_MONITORING_METRIC_TOOLTIPS,
  PORTFOLIO_MONITORING_PAYMENT_FIELD_TOOLTIPS,
} from 'api/portfolioMonitoringConfigTooltips';
import ContentBox from 'components/ContentBox';
import { HelperTooltip } from 'components/Tooltip';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

const ROAS_COVENANT_KEY = 'roas_covenant_prediction';
const ROAS_MOM_KEY = 'roas_mom';
const REFUND_ANOMALY_KEY = 'refund_return_anomalies';
const CAC_KEYS = new Set(['current_cac', 'last_month_cac']);
const PAYMENT_STATUS_KEY = 'payment_status';

function PaymentBoundaryInputs({
  readOnly,
  metric,
  index,
  boundary,
  updateMetric,
}) {
  const isGreen = boundary === 'green';
  const daysField = isGreen ? 'greenMaxDaysLate' : 'amberMaxDaysLate';
  const amountField = isGreen ? 'greenMaxAmountDiffPct' : 'amberMaxAmountDiffPct';
  const daysDefault = isGreen ? 0 : 7;
  const amountDefault = isGreen ? 0 : 2;
  const daysTooltip = isGreen
    ? PORTFOLIO_MONITORING_PAYMENT_FIELD_TOOLTIPS.greenDaysLate
    : PORTFOLIO_MONITORING_PAYMENT_FIELD_TOOLTIPS.amberDaysLate;
  const amountTooltip = isGreen
    ? PORTFOLIO_MONITORING_PAYMENT_FIELD_TOOLTIPS.greenAmountDiffPct
    : PORTFOLIO_MONITORING_PAYMENT_FIELD_TOOLTIPS.amberAmountDiffPct;

  return (
    <VStack align="stretch" spacing={2}>
      <HStack spacing={1}>
        <Text fontSize="xs" color="gray.600" minW="5.5rem">
          Days late
        </Text>
        <HelperTooltip content={daysTooltip} placement="top" />
        <Input
          type="number"
          size="sm"
          w="16"
          value={metric[daysField] ?? daysDefault}
          isDisabled={readOnly}
          onChange={(e) => updateMetric(index, daysField, e.target.value)}
        />
      </HStack>
      <HStack spacing={1}>
        <Text fontSize="xs" color="gray.600" minW="5.5rem">
          Amount diff %
        </Text>
        <HelperTooltip content={amountTooltip} placement="top" />
        <Input
          type="number"
          size="sm"
          w="16"
          value={metric[amountField] ?? amountDefault}
          isDisabled={readOnly}
          onChange={(e) => updateMetric(index, amountField, e.target.value)}
        />
      </HStack>
    </VStack>
  );
}

function PortfolioMonitoringConfig() {
  const { customerId } = useParams();
  const toast = useToast();
  const saveModal = useDisclosure();
  const [loading, setLoading] = useState(true);
  const [latest, setLatest] = useState(null);
  const [summaries, setSummaries] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState('edit');
  const [viewDoc, setViewDoc] = useState(null);
  const [draftMetrics, setDraftMetrics] = useState([]);
  const [saveNote, setSaveNote] = useState('');
  const [saving, setSaving] = useState(false);

  const bootstrap = async () => {
    setLoading(true);
    try {
      let lat = null;
      try {
        lat = await getLatestPortfolioMonitoringConfig(customerId);
      } catch (err) {
        if (err?.response?.status !== 404) {
          throw err;
        }
      }
      const sum = await listPortfolioMonitoringConfigVersions(customerId);
      setLatest(lat);
      setSummaries(sum || []);
      setSelectedVersion('edit');
      setViewDoc(null);
      setDraftMetrics(
        mergePortfolioMonitoringMetrics(JSON.parse(JSON.stringify(lat?.metrics || []))),
      );
    } catch (e) {
      toast({
        title: 'Error',
        description: e?.response?.data?.error || e.message,
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    bootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const onVersionSelect = async (value) => {
    if (value === 'edit') {
      setSelectedVersion('edit');
      setViewDoc(null);
      setDraftMetrics(
        mergePortfolioMonitoringMetrics(JSON.parse(JSON.stringify(latest?.metrics || []))),
      );
      return;
    }
    const v = Number(value);
    setSelectedVersion(v);
    try {
      const doc = await getPortfolioMonitoringConfigVersion(customerId, v);
      setViewDoc({
        ...doc,
        metrics: mergePortfolioMonitoringMetrics(doc?.metrics || []),
      });
    } catch (e) {
      toast({
        title: 'Error',
        description: e?.response?.data?.error || e.message,
        status: 'error',
      });
    }
  };

  const displayMetrics =
    selectedVersion === 'edit' ? draftMetrics : viewDoc?.metrics || [];
  const readOnly = selectedVersion !== 'edit';

  const updateMetric = (index, field, rawValue) => {
    if (readOnly) {
      return;
    }
    setDraftMetrics((prev) => {
      const next = [...prev];
      const row = { ...next[index] };
      if (field === 'higherIsBetter') {
        row.higherIsBetter = Boolean(rawValue);
      } else if (
        field === 'greenMinPct' ||
        field === 'amberMinPct' ||
        field === 'greenMaxDaysLate' ||
        field === 'greenMaxAmountDiffPct' ||
        field === 'amberMaxDaysLate' ||
        field === 'amberMaxAmountDiffPct'
      ) {
        const n = Number(rawValue);
        row[field] = Number.isFinite(n) ? n : 0;
      } else {
        row[field] = rawValue;
      }
      next[index] = row;
      return next;
    });
  };

  const onSave = async () => {
    setSaving(true);
    try {
      await createPortfolioMonitoringConfigVersion(customerId, {
        metrics: draftMetrics,
        note: saveNote.trim() || undefined,
      });
      saveModal.onClose();
      setSaveNote('');
      await bootstrap();
      toast({ title: 'Saved', status: 'success' });
    } catch (e) {
      toast({
        title: 'Save failed',
        description: e?.response?.data?.error || e.message,
        status: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <ContentBox>
        <Text>Loading…</Text>
      </ContentBox>
    );
  }

  return (
    <ContentBox>
      <HStack justify="space-between" mb={6}>
        <Heading size="md">Portfolio Monitoring</Heading>
        <HStack>
          <Select
            w="220px"
            value={String(selectedVersion)}
            onChange={(e) => onVersionSelect(e.target.value)}
          >
            <option value="edit">Edit latest (v{latest?.version || '—'})</option>
            {(summaries || []).map((s) => (
              <option key={s.id} value={String(s.version)}>
                v{s.version} — {new Date(s.createdAt).toLocaleString()}
              </option>
            ))}
          </Select>
          {!readOnly && (
            <Button variant="brand" onClick={saveModal.onOpen}>
              Save new version
            </Button>
          )}
        </HStack>
      </HStack>

      <Text fontSize="sm" color="gray.600" mb={4}>
        Configure green / amber / red bands for this customer&apos;s rows in the
        portfolio monitoring Excel. Compare metrics use % change vs prior period.
        CAC rows use % of max CAC. ROAS covenant (actual and prediction share
        one config, also used by ROAS MO, MOB %) and ROAS MoM use headroom as a
        percent of the anchor (anchor 12% and green 20 → ROAS about 14.4% is
        green). Refund anomalies use percentage points, last week versus the
        prior 8 weeks. Payment status uses max days
        late and max amount difference % for green and amber (defaults: green
        0d / 0%, amber 7d / 2%).
      </Text>

      <Table size="sm">
        <Thead>
          <Tr>
            <Th>
              Metric
            </Th>
            <Th>
              <HStack spacing={0} display="inline-flex">
                <Text>Higher is better</Text>
                <HelperTooltip
                  content={PORTFOLIO_MONITORING_COLUMN_TOOLTIPS.higherIsBetter}
                  placement="top"
                />
              </HStack>
            </Th>
            <Th>
              <HStack spacing={0} display="inline-flex">
                <Text>Green boundary</Text>
                <HelperTooltip
                  content={PORTFOLIO_MONITORING_COLUMN_TOOLTIPS.greenBoundary}
                  placement="top"
                />
              </HStack>
            </Th>
            <Th>
              <HStack spacing={0} display="inline-flex">
                <Text>Amber boundary</Text>
                <HelperTooltip
                  content={PORTFOLIO_MONITORING_COLUMN_TOOLTIPS.amberBoundary}
                  placement="top"
                />
              </HStack>
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {displayMetrics.map((metric, index) => {
            const isPaymentStatus = metric.key === PAYMENT_STATUS_KEY;
            const isRoasBand =
              metric.key === ROAS_COVENANT_KEY || metric.key === ROAS_MOM_KEY;
            const isRefundAnomaly = metric.key === REFUND_ANOMALY_KEY;
            const isCac = CAC_KEYS.has(metric.key);
            const pctSuffix = isRoasBand ? '%' : isRefundAnomaly ? ' pp' : isCac ? ' % of max' : '%';
            return (
              <Tr key={metric.key}>
                <Td>
                  <HStack align="flex-start" spacing={0}>
                    <Box>
                      {metric.label}
                      {isRoasBand && (
                        <Text fontSize="xs" color="gray.500">
                          headroom vs anchor (%)
                        </Text>
                      )}
                      {isRefundAnomaly && (
                        <Text fontSize="xs" color="gray.500">
                          last week vs prior 8 weeks (pp)
                        </Text>
                      )}
                      {isCac && (
                        <Text fontSize="xs" color="gray.500">
                          vs max CAC (% offset)
                        </Text>
                      )}
                    </Box>
                    {PORTFOLIO_MONITORING_METRIC_TOOLTIPS[metric.key] && (
                      <HelperTooltip
                        content={PORTFOLIO_MONITORING_METRIC_TOOLTIPS[metric.key]}
                        placement="top"
                      />
                    )}
                  </HStack>
                </Td>
                <Td>
                  {isPaymentStatus ? (
                    <Text fontSize="sm" color="gray.500">
                      —
                    </Text>
                  ) : (
                    <Switch
                      isChecked={metric.higherIsBetter}
                      isDisabled={readOnly || isRoasBand}
                      onChange={(e) =>
                        updateMetric(index, 'higherIsBetter', e.target.checked)
                      }
                    />
                  )}
                </Td>
                <Td>
                  {isPaymentStatus ? (
                    <PaymentBoundaryInputs
                      readOnly={readOnly}
                      metric={metric}
                      index={index}
                      boundary="green"
                      updateMetric={updateMetric}
                    />
                  ) : (
                    <HStack spacing={1}>
                      <Input
                        type="number"
                        size="sm"
                        w="20"
                        value={metric.greenMinPct ?? 0}
                        isDisabled={readOnly}
                        onChange={(e) =>
                          updateMetric(index, 'greenMinPct', e.target.value)
                        }
                      />
                      <Text fontSize="xs" color="gray.500">
                        {pctSuffix}
                      </Text>
                    </HStack>
                  )}
                </Td>
                <Td>
                  {isPaymentStatus ? (
                    <PaymentBoundaryInputs
                      readOnly={readOnly}
                      metric={metric}
                      index={index}
                      boundary="amber"
                      updateMetric={updateMetric}
                    />
                  ) : (
                    <HStack spacing={1}>
                      <Input
                        type="number"
                        size="sm"
                        w="20"
                        value={metric.amberMinPct ?? 0}
                        isDisabled={readOnly}
                        onChange={(e) =>
                          updateMetric(index, 'amberMinPct', e.target.value)
                        }
                      />
                      <Text fontSize="xs" color="gray.500">
                        {pctSuffix}
                      </Text>
                    </HStack>
                  )}
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>

      <Modal isOpen={saveModal.isOpen} onClose={saveModal.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Save portfolio monitoring config</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel>Note (optional)</FormLabel>
              <Textarea
                value={saveNote}
                onChange={(e) => setSaveNote(e.target.value)}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={saveModal.onClose}>
              Cancel
            </Button>
            <Button variant="brand" isLoading={saving} onClick={onSave}>
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </ContentBox>
  );
}

export default PortfolioMonitoringConfig;
